import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { TimestampDisplay } from '@/components/ui/timestamp-display';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { AuditLogResponse } from '@/api/audit-logs';
import { reasonLabel } from '@/lib/reason-codes';

interface AuditLogMetadata {
  accountName?: string;
  locationName?: string;
  partnerTypeCode?: string;
  syncType?: string;
  jobId?: string;
  action?: string;
  scoutaroundId?: string | number;
  adminEmail?: string;
  /** DISABLE entries: false when some locations were still published, so the account stayed enabled. */
  completed?: boolean;
  /** DISABLE entries that did not complete: the locations that could not be unpublished (capped). */
  failures?: unknown;
  /** REMOVE_IMAGE entries: the removed image, its 0-based gallery position, and what is left. */
  imageUrl?: string;
  imageIndex?: number;
  remainingImageCount?: number;
}

/** Readable names for metadata keys shown in the details column. */
const METADATA_LABELS: Record<string, string> = {
  locationsUnpublished: 'Locations unpublished',
  locationsFailed: 'Locations failed',
  stillPublished: 'Still published',
  batchOperationId: 'Batch operation',
  syncPaused: 'Sync paused',
  reason: 'Reason',
  reasonCode: 'Reason',
  reasonNote: 'Note',
  note: 'Note',
  errorMessage: 'Error',
  published: 'Published',
  publishedUpdate: 'ScoutAround update',
  scoutaroundImageId: 'SA image ID',
  // The key predates the mapping-only unpublish; it counts Bridge mapping rows removed.
  imagesDeleted: 'Image mappings removed',
};

interface AuditFailure {
  syncedLocationId?: string;
  name?: string;
  reason?: string;
}

/** The failure list of a DISABLE entry, tolerating any shape a stored row may have. */
function getFailures(metadata: AuditLogMetadata | null): AuditFailure[] {
  const failures = metadata?.failures;
  if (!Array.isArray(failures)) return [];
  return failures.filter((f): f is AuditFailure => typeof f === 'object' && f !== null);
}

// Extract admin name from email (part before @)
function getAdminDisplayName(email: string): string {
  const namePart = email.split('@')[0];
  if (!namePart) return email;
  // Convert dot/underscore notation to readable name (e.g., "john.doe" -> "John Doe")
  return namePart
    .replace(/[._]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

interface AuditLogListProps {
  logs: AuditLogResponse[];
  isLoading?: boolean;
}

function getActionBadge(action: string, metadata: AuditLogMetadata | null) {
  if (action === 'disable' && metadata?.completed === false) {
    return (
      <Badge variant="destructive" data-testid="audit-action-disable-incomplete">
        Disable incomplete
      </Badge>
    );
  }

  const variants: Record<string, 'default' | 'success' | 'destructive' | 'secondary' | 'outline'> = {
    connect: 'success',
    disconnect: 'destructive',
    reconnect: 'default',
    enable: 'success',
    disable: 'secondary',
    start_sync: 'default',
    pause_sync: 'secondary',
    resume_sync: 'default',
    enrich: 'default',
    publish: 'success',
    unpublish: 'destructive',
    remove_image: 'destructive',
    update_account_settings: 'default',
    ai_content_override: 'secondary',
    regenerate_ai_content: 'default',
    classify_location: 'default',
    dismiss_duplicate: 'secondary',
    auto_unpublish: 'destructive',
  };

  const labels: Record<string, string> = {
    connect: 'Connected',
    disconnect: 'Disconnected',
    reconnect: 'Reconnected',
    enable: 'Enabled',
    disable: 'Disabled',
    start_sync: 'Started Sync',
    pause_sync: 'Paused Sync',
    resume_sync: 'Resumed Sync',
    enrich: 'Enriched',
    publish: 'Published',
    unpublish: 'Unpublished',
    remove_image: 'Removed Image',
    update_account_settings: 'Settings Changed',
    ai_content_override: 'AI Content Override',
    regenerate_ai_content: 'AI Content Regenerated',
    classify_location: 'Classified with AI',
    dismiss_duplicate: 'Duplicate Dismissed',
    auto_unpublish: 'Auto-unpublished',
  };

  return (
    <Badge variant={variants[action] ?? 'outline'} data-testid={`audit-action-${action}`}>
      {labels[action] ?? action}
    </Badge>
  );
}

function FailureList({ logId, failures }: { logId: string; failures: AuditFailure[] }): JSX.Element {
  return (
    <details className="mt-1" data-testid={`audit-log-failures-${logId}`}>
      <summary className="cursor-pointer text-xs font-medium text-destructive">
        {failures.length} location{failures.length === 1 ? '' : 's'} could not be unpublished
      </summary>
      <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs">
        {failures.map((failure, index) => (
          <li key={failure.syncedLocationId ?? index} data-testid="audit-log-failure">
            {failure.name ? `${failure.name} ` : ''}
            {failure.syncedLocationId ? `(#${failure.syncedLocationId})` : ''}
            {failure.reason ? `: ${failure.reason}` : ''}
          </li>
        ))}
      </ul>
    </details>
  );
}

// Format metadata into readable details (includes entity name now since we removed Entity column)
function formatMetadataDetails(
  entityType: string,
  metadata: AuditLogMetadata | null
): string {
  if (!metadata || Object.keys(metadata).length === 0) {
    return '-';
  }

  const details: string[] = [];

  // Add entity name first (this was previously in a separate column)
  if (entityType === 'partner_account' && metadata.accountName) {
    details.push(`Account: ${metadata.accountName}`);
  }
  if (entityType === 'synced_location' && metadata.locationName) {
    details.push(`Location: ${metadata.locationName}`);
  }

  // Keys to skip as they're shown elsewhere (`completed` is the action badge,
  // `failures` the expandable list)
  const skipKeys = [
    'accountName',
    'locationName',
    'adminEmail',
    'partnerTypeCode',
    'syncType',
    'action',
    'scoutaroundId',
    'jobId',
    'completed',
    'imageUrl',
    'imageIndex',
    'remainingImageCount',
  ];

  if (metadata.partnerTypeCode) {
    details.push(`Partner: ${metadata.partnerTypeCode}`);
  }

  if (metadata.syncType) {
    details.push(`Sync: ${metadata.syncType}`);
  }

  if (metadata.action) {
    details.push(`Action: ${metadata.action}`);
  }

  if (metadata.scoutaroundId) {
    details.push(`SA ID: ${String(metadata.scoutaroundId)}`);
  }

  if (metadata.imageUrl) {
    const position = metadata.imageIndex !== undefined ? ` ${String(metadata.imageIndex + 1)}` : '';
    details.push(`Image${position}: ${metadata.imageUrl}`);
  }

  if (metadata.remainingImageCount !== undefined) {
    details.push(`${String(metadata.remainingImageCount)} image(s) remain`);
  }

  // Add any other fields that aren't already displayed
  for (const [key, value] of Object.entries(metadata)) {
    if (skipKeys.includes(key)) {
      continue;
    }
    const label = METADATA_LABELS[key] ?? key;
    if (key === 'reasonCode' && typeof value === 'string') {
      details.push(`${label}: ${reasonLabel(value)}`);
    } else if (typeof value === 'string') {
      details.push(`${label}: ${value}`);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      details.push(`${label}: ${String(value)}`);
    }
    // Skip objects, arrays, null, undefined
  }

  return details.length > 0 ? details.join(' | ') : '-';
}

export function AuditLogList({ logs, isLoading }: AuditLogListProps): JSX.Element {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 10 }, (_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No audit logs found.
      </p>
    );
  }

  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Timestamp</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Admin</TableHead>
            <TableHead>Details</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => {
            const metadata = log.metadata as AuditLogMetadata | null;
            const adminEmail = metadata?.adminEmail;
            const adminDisplayName = adminEmail ? getAdminDisplayName(adminEmail) : null;
            const failures = getFailures(metadata);

            return (
              <TableRow key={log.id} data-testid={`audit-log-row-${log.id}`}>
                <TableCell>
                  <TimestampDisplay timestamp={log.createdAt} className="text-sm" />
                </TableCell>
                <TableCell>{getActionBadge(log.actionType, metadata)}</TableCell>
                <TableCell>
                  {log.adminId === 0 ? (
                    <Badge variant="outline">System</Badge>
                  ) : adminEmail ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default text-sm font-medium">
                          {adminDisplayName}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{adminEmail}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      Admin #{String(log.adminId)}
                    </span>
                  )}
                </TableCell>
                <TableCell className="max-w-md wrap-anywhere text-sm text-muted-foreground">
                  {formatMetadataDetails(log.entityType, metadata)}
                  {failures.length > 0 && <FailureList logId={log.id} failures={failures} />}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
}
